import {createAsyncThunk} from '@reduxjs/toolkit'

import {isEqual} from '@shared/helpers/lang'
import {pick} from '@shared/helpers/object'

import {historyChange, systemConfirm} from '@web/actions/application'
import {getSavedStateFromStorage} from '@web/actions/restoreState'
import {APIStatus, OrderEntry, PayMethod} from '@web/core/constants'
import {getUrlInfo} from '@web/helpers/path'
import {isHopeSellerGuarantee} from '@web/helpers/policies'
import {TranslationSetType} from '@web/i18n'
import {languageSelector} from '@web/slices/common/slice'
import {locationSelector} from '@web/slices/router'

import {NO_STORAGE_ON_REFRESH} from './config'
import {RootState, SyncState, ThunkApi} from './interfaces/store'

function getDiffInfo(storageState: SyncState, appState: RootState, {isBranch}: {isBranch: boolean}) {
    const getDiff = (paths: string[]) => {
        const diff = pick(paths)
        return !isEqual(diff(appState), diff(storageState))
    }

    const result = {
        isAPISuccess: storageState.common.scene === APIStatus.RESOLVED,
        /** 배송지 비교 목록 */
        isDiffDelivery:
            getDiff(['deliveryInfo.pickup', 'deliveryInfo.scene']) ||
            /**
             * 장보기 배송의 경우 배송정보를 복원하지 않기때문에 필터링 추가
             * @see https://oss.fin.navercorp.com/payment-fe/order-web/issues/19258
             **/
            (!isBranch && getDiff(['deliveryInfo.selected.addressInfo'])) ||
            getDiff(['deliveryInfo.selected.recipient']),
        /** 메모 목록 */
        isDiffMemo: getDiff(['deliveryInfo.memo']),
        /** 통관고유부호 목록 */
        isDiffPersonalCustomsClearance: getDiff(['personalCustomsClearance.selected']),
        /** 수신자 비교 목록 */
        isDiffReceiveInfo: getDiff([
            'receiverInfo.receiverName',
            'receiverInfo.phoneNumber',
            'receiverInfo.usedAutofill',
        ]),
        /** 쿠폰 비교 목록 */
        isDiffCoupon: getDiff(['orderSheetCoupon.selected']),
        /** 네이버 포인트 목록 */
        isDiffNaverPoint: getDiff(['pointPayment.naverPayPoint.usingAmount', 'pointPayment.naverPayPoint.usePointAll']),
        isDiffPreferredPoint: getDiff(['pointPayment.naverPayPoint.preferredPoint']),
        /** 후불결제 목록 */
        isDiffPayLater: getDiff(['payLater.enabled', 'payLater.useAll', 'payLater.usingAmount']),
        isDiffRecharge:
            storageState.paymentMethod.selected.payMethods.includes(PayMethod.RECHARGE_POINT) &&
            storageState.chargePoint.recharging.amount !== appState.chargePoint.recharging.amount,
        isDiffHopeDate: storageState.orderSheetProducts?.some((product, index) => {
            const appProduct = appState.orderSheetProducts?.[index]
            return (
                isHopeSellerGuarantee(product.deliveryInfo.deliveryAttributeType) &&
                product.arrivalGuaranteeDateTime !== appProduct?.arrivalGuaranteeDateTime
            )
        }),
    }
    const differences = [
        ...(result.isAPISuccess ? [] : ['REJECTED']),
        ...(result.isDiffDelivery ? ['DELIVERY'] : []),
        ...(result.isDiffPersonalCustomsClearance ? ['PERSONAL_CUSTOMS_CLEARANCE'] : []),
        ...(result.isDiffReceiveInfo ? ['RECEIVER_INFO'] : []),
        ...(result.isDiffMemo ? ['MEMO'] : []),
        ...(result.isDiffCoupon ? ['COUPON'] : []),
        ...(result.isDiffNaverPoint ? ['NAVER_POINT'] : []),
        ...(result.isDiffPreferredPoint ? ['PREFERRED_POINT'] : []),
        ...(result.isDiffPayLater ? ['PAY_LATER'] : []),
        ...(result.isDiffRecharge ? ['RECHARGE'] : []),
        ...(result.isDiffHopeDate ? ['HOPE_DATE_CHANGED'] : []),
    ]
    return {
        ...result,
        differences,
        // 조회 중 값을 저장했다면, 복원하지 않음
        shouldRestore:
            result.isAPISuccess &&
            [
                result.isDiffDelivery,
                result.isDiffPersonalCustomsClearance,
                result.isDiffReceiveInfo,
                result.isDiffMemo,
                result.isDiffCoupon,
                result.isDiffNaverPoint,
                result.isDiffPreferredPoint,
                result.isDiffPayLater,
                result.isDiffRecharge,
                result.isDiffHopeDate,
            ].some((bool) => bool),
    }
}

function getSearchFromInfo(search: string) {
    return {
        isFromGifting: search.includes('from=gifting'),
    }
}

const hasDeliveryManagerScene = (
    locationState: unknown,
): locationState is {deliveryManagerScene: string; [key: string]: string} => {
    return !!locationState && typeof locationState === 'object' && 'deliveryManagerScene' in locationState
}

/** 다국어 적용 언어셋 */
const translation: TranslationSetType = {
    ko: {
        복원문구: '주문서가 새로고침 되었습니다. 이전에 입력하신 정보(쿠폰, 포인트, 배송지)를 그대로 유지하시겠습니까?',
    },
    en: {
        복원문구: 'The order page has been refreshed. Would you like to retain the previously entered information?',
    },
    ja: {
        복원문구: '注文内容が変更されました。以前に入力した情報を維持しますか？',
    },
    zh: {
        복원문구: '订单页面已刷新。是否保留您之前输入的信息？',
    },
}

/** storage 전체 복원 */
export const initOrderSheetByStorage = createAsyncThunk<
    {
        data: SyncState
        isBranch: boolean
        isRestore: boolean
        isCouponFailed?: boolean
        differences: string[]
    },
    {
        orderSheetId: string
        /**
         * @see https://oss.fin.navercorp.com/payment-fe/order-web/issues/13229
         * 쿠폰조회 API 실패 여부
         * 조회 시 coupon.fallback 이 true로 내려왔을때는
         * 복원 시 orderSheetCoupon과 orderTotal의
         * 할인필드들을 반영하지 않는다.
         */
        isCouponFailed?: boolean
    },
    ThunkApi<{
        case: string
        differences: string[]
    }>
>('initOrderSheetByStorage', async ({orderSheetId, isCouponFailed}, {dispatch, getState, rejectWithValue}) => {
    const appState = getState()
    const storageState = getSavedStateFromStorage<SyncState>({
        saverKey: orderSheetId,
        secureStorageKey: orderSheetId,
    })
    const {search, state: locationState} = locationSelector(appState)
    const {language} = languageSelector(appState)

    const {isFromGifting} = getSearchFromInfo(search)

    const deliveryManagerScene = hasDeliveryManagerScene(locationState) ? locationState.deliveryManagerScene : null

    if (NO_STORAGE_ON_REFRESH) {
        return rejectWithValue({
            case: 'RESTORE_NO_STORAGE_OFF_REFRESH',
            differences: [],
        })
    }

    if (isFromGifting) {
        // 선물하기 친구 선택 주문서 진입시 query 를 달고 진입합니다.
        // 이때 모든 복원을 하지 않습니다.
        // @ref https://oss.fin.navercorp.com/payOrder/order-qa/issues/4845#issuecomment-819921
        // (+) from=gifting query만 제거되도록 수정
        // @ref https://oss.fin.navercorp.com/Product-Planning/Order_Plan/issues/2442#issuecomment-2034726
        const urlInfo = getUrlInfo({url: window.location.href, removeQuery: ['from']})

        dispatch(historyChange({type: 'REPLACE_QUERY', url: urlInfo.url}))

        return rejectWithValue({case: 'RESTORE_GIFT_PASS', differences: []})
    }
    if (storageState) {
        const isBranch = appState.common.entry === OrderEntry.BRANCH
        const isGifting = appState.common.entry === OrderEntry.GIFTING
        const {isAPISuccess, differences, shouldRestore} = getDiffInfo(storageState, getState(), {isBranch})

        // 받는 사람 정보가 변경되면 선물 및 쿠폰 등의 정보가 달라지기 때문에 복원을 하지 않습니다.
        if (isGifting && storageState.gift.sessionKey && appState.gift.sessionKey !== storageState.gift.sessionKey) {
            return rejectWithValue({case: 'RESTORE_GIFT_CHANGE_PASS', differences: []})
        }

        // 얼럿 없이 자동 복원 케이스. 추후 자동복원 필요 스펙이 늘어나면 통합 관리 필요
        if (deliveryManagerScene) {
            return {
                data: storageState,
                isBranch,
                isRestore: true,
                differences,
            }
        }

        if (shouldRestore) {
            const isRestore = await new Promise<boolean>((resolve) => {
                dispatch(
                    systemConfirm({
                        type: 'CONFIRM',
                        message:
                            translation[language]['복원문구'] ??
                            '주문서가 새로고침 되었습니다. 이전에 입력하신 정보(쿠폰, 포인트, 배송지)를 그대로 유지하시겠습니까?',
                        callback: (confirmed: boolean) => {
                            resolve(confirmed)
                        },
                    }),
                )
            })

            if (isRestore) {
                return {
                    data: storageState,
                    isBranch,
                    isRestore,
                    differences,
                    isCouponFailed,
                }
            }
            return rejectWithValue({
                case: 'RESTORE_CANCEL',
                differences,
            })
        }

        return rejectWithValue({
            case: 'RESTORE_SAME',
            differences: [...(isAPISuccess ? [] : ['REJECTED'])],
        })
    }
    return rejectWithValue({case: 'RESTORE_NOT_FOUND', differences: []})
})
